import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  makeStyles
} from "@material-ui/core";
import {
  FileCopy,
  CheckCircle,
  SettingsEthernet,
  Web,
  Add,
  Delete,
  Edit
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  configPaper: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    marginBottom: theme.spacing(3),
  },
  configField: {
    marginBottom: theme.spacing(2),
  },
  copyButton: {
    padding: 4,
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    marginBottom: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
  applicationsList: {
    marginTop: theme.spacing(2),
  },
  applicationItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
  }
}));

const IntegrationConfig = ({
  integrationData,
  applicationsData,
  protocol,
  hostingType,
  clientData,
  onIntegrationUpdate,
  onApplicationsUpdate
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    clusterRegion: "",
    clusterName: "",
    privateDns: ""
  });
  const [errors, setErrors] = useState({});

  // Auto-generate integration details if not present
  React.useEffect(() => {
    if (protocol && clientData && clientData.clientName && !integrationData.spEntityId) {
      // Use client name to generate URLs
      const clientName = clientData.clientName.toLowerCase().replace(/[^a-z0-9]/g, '');

      const autoGeneratedData = {
        spEntityId: `https://authpd1.mywizard-aiops.accenture.com/realms/accenturepd-${clientName}`,
        acsUrl: `https://authpd1.mywizard-aiops.accenture.com/realms/accenturepd-${clientName}/broker/samlprod/endpoint`,
        singleLogoutUrl: `https://authpd1.mywizard-aiops.accenture.com/realms/accenturepd-${clientName}/broker/samlprod/endpoint`
      };
      onIntegrationUpdate(autoGeneratedData);
    }
  }, [protocol, clientData, integrationData.spEntityId, onIntegrationUpdate]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a small toast notification here
    });
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const validatePrivateDns = (dns) => {
    // AWS EC2 internal DNS formats:
    // 1. ip-x-x-x-x.ec2.internal (US regions)
    // 2. ip-x-x-x-x.region.compute.internal (other regions)
    // 3. domainname-x-x-x-x.region.compute.internal (custom formats)

    const patterns = [
      // Standard EC2 internal format (US regions): ip-172-31-45-123.ec2.internal
      /^ip-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}\.ec2\.internal$/,

      // Regional compute internal format: ip-172-31-45-123.us-west-2.compute.internal
      /^ip-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}\.[a-z0-9-]+\.compute\.internal$/,

      // Custom DNS with IP pattern: domainname-172-31-45-123.region.compute.internal
      /^[a-z0-9-]+-\d{1,3}-\d{1,3}-\d{1,3}-\d{1,3}\.[a-z0-9-]+\.compute\.internal$/,

      // General internal AWS DNS pattern: anything.compute.internal or anything.ec2.internal
      /^[a-z0-9.-]+\.(compute|ec2)\.internal$/
    ];

    return patterns.some(pattern => pattern.test(dns.toLowerCase()));
  };

  const handleAddApp = () => {
    setFormData({
      name: "",
      url: "",
      clusterRegion: "",
      clusterName: "",
      privateDns: ""
    });
    setEditingIndex(null);
    setErrors({});
    setDialogOpen(true);
  };

  const handleEditApp = (index) => {
    setFormData(applicationsData[index]);
    setEditingIndex(index);
    setErrors({});
    setDialogOpen(true);
  };

  const handleDeleteApp = (index) => {
    const newData = applicationsData.filter((_, i) => i !== index);
    onApplicationsUpdate(newData);
  };

  const handleSaveApp = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Application name is required";
    }

    if (!formData.url.trim()) {
      newErrors.url = "Application URL is required";
    } else if (!validateUrl(formData.url)) {
      newErrors.url = "Please enter a valid URL (must start with http:// or https://)";
    }

    // Validate hosting-specific fields
    if (hostingType === "Shared") {
      if (!formData.clusterRegion.trim()) {
        newErrors.clusterRegion = "Cluster region is required for shared hosting";
      }
      if (!formData.clusterName.trim()) {
        newErrors.clusterName = "Cluster name is required for shared hosting";
      }
    } else if (hostingType === "Dedicated") {
      if (!formData.privateDns.trim()) {
        newErrors.privateDns = "Private DNS is required for dedicated hosting";
      } else if (!validatePrivateDns(formData.privateDns.trim())) {
        newErrors.privateDns = "Please enter a valid AWS internal DNS (e.g., ip-x-x-x-x.ec2.internal or ip-x-x-x-x.region.compute.internal)";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let newData;
    if (editingIndex !== null) {
      newData = [...applicationsData];
      newData[editingIndex] = formData;
    } else {
      newData = [...applicationsData, formData];
    }

    onApplicationsUpdate(newData);
    setDialogOpen(false);
  };

  const handleCancelApp = () => {
    setDialogOpen(false);
    setFormData({
      name: "",
      url: "",
      clusterRegion: "",
      clusterName: "",
      privateDns: ""
    });
    setErrors({});
  };

  const renderSAMLConfig = () => (
    <Paper className={classes.configPaper}>
      <Typography variant="h6" style={{ marginBottom: 16, color: '#1976d2' }}>
        SAML Service Provider Configuration
      </Typography>

      <Chip
        label="Auto-Generated Configuration"
        className={classes.successChip}
        icon={<CheckCircle />}
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              SP Entity ID
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                value={integrationData.spEntityId}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  className={classes.copyButton}
                  onClick={() => handleCopy(integrationData.spEntityId)}
                >
                  <FileCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Assertion Consumer Service (ACS) URL
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                value={integrationData.acsUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  className={classes.copyButton}
                  onClick={() => handleCopy(integrationData.acsUrl)}
                >
                  <FileCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Single Logout URL
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                value={integrationData.singleLogoutUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  className={classes.copyButton}
                  onClick={() => handleCopy(integrationData.singleLogoutUrl)}
                >
                  <FileCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderOIDCConfig = () => (
    <Paper className={classes.configPaper}>
      <Typography variant="h6" style={{ marginBottom: 16, color: '#1976d2' }}>
        OIDC Application Configuration
      </Typography>

      <Chip
        label="Auto-Generated Configuration"
        className={classes.successChip}
        icon={<CheckCircle />}
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Redirect URI
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                value={integrationData.acsUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  className={classes.copyButton}
                  onClick={() => handleCopy(integrationData.acsUrl)}
                >
                  <FileCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Post Logout Redirect URI
            </Typography>
            <Box display="flex" alignItems="center">
              <TextField
                fullWidth
                value={integrationData.singleLogoutUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  className={classes.copyButton}
                  onClick={() => handleCopy(integrationData.singleLogoutUrl)}
                >
                  <FileCopy />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.configField}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Required Scopes
            </Typography>
            <TextField
              fullWidth
              value="openid profile email"
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
        </Grid>
      </Grid>
    </Paper>
  );


  return (
    <Box className={classes.root}>
      {/* Integration Details Section */}
      <div className={classes.sectionHeader}>
        <SettingsEthernet className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Integration Configuration
        </Typography>
      </div>

      {!protocol ? (
        <Paper className={classes.configPaper}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Please complete the Identity Provider configuration in the previous step to generate integration details.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Once you select a protocol (SAML or OIDC), the system will automatically generate the required URLs and configuration values for your integration.
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" paragraph>
            Configuration values automatically generated for your {protocol} integration.
            Use these values when configuring your Identity Provider.
          </Typography>

          <div className={classes.infoBox}>
            <Typography variant="body2" color="textPrimary">
              <strong>Next Steps:</strong> Copy these values and configure them in your {protocol === "SAML" ? "SAML Service Provider" : "OIDC Application"} settings.
            </Typography>
          </div>

          {protocol === "SAML" ? renderSAMLConfig() : renderOIDCConfig()}
        </>
      )}

      <Divider className={classes.divider} />

      {/* Applications Section */}
      <div className={classes.sectionHeader}>
        <Web className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Application Configuration
        </Typography>
      </div>

      {!hostingType ? (
        <Paper className={classes.configPaper}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Please complete the Client Setup step and select a hosting type before configuring applications.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Once you select a hosting type (Shared or Dedicated), you'll be able to add applications that will use SSO authentication.
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Configure applications that will use SSO authentication.
            For <strong>{hostingType.toLowerCase()} hosting</strong>, you'll need to provide {hostingType === "Shared" ? "cluster details" : "private DNS"} for each application.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddApp}
            className={classes.addButton}
            size="small"
          >
            Add Application
          </Button>
        </>
      )}

      {hostingType && applicationsData.length === 0 ? (
        <div className={classes.emptyState}>
          <Web style={{ fontSize: 36, marginBottom: 8 }} color="disabled" />
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            No applications configured
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add applications to enable SSO authentication.
          </Typography>
        </div>
      ) : hostingType && (
        <List className={classes.applicationsList}>
          {applicationsData.map((app, index) => (
            <ListItem
              key={index}
              className={classes.applicationItem}
              dense
            >
              <ListItemText
                primary={app.name}
                secondary={
                  <div>
                    <div>{app.url}</div>
                    {hostingType === "Shared" && app.clusterRegion && (
                      <div style={{ fontSize: '0.75rem', color: 'gray' }}>
                        Cluster: {app.clusterRegion} / {app.clusterName}
                      </div>
                    )}
                    {hostingType === "Dedicated" && app.privateDns && (
                      <div style={{ fontSize: '0.75rem', color: 'gray' }}>
                        Private DNS: {app.privateDns}
                      </div>
                    )}
                  </div>
                }
                primaryTypographyProps={{ variant: "subtitle2", color: "primary" }}
                secondaryTypographyProps={{ variant: "body2" }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleEditApp(index)}
                  size="small"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteApp(index)}
                  color="secondary"
                  size="small"
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Add/Edit Application Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelApp}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex !== null ? "Edit Application" : "Add New Application"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Application Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                variant="outlined"
                size="small"
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Application URL"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                variant="outlined"
                size="small"
                error={!!errors.url}
                helperText={errors.url || "Must be a valid URL (https://...)"}
                required
              />
            </Grid>

            {/* Hosting-specific fields */}
            {hostingType === "Shared" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cluster Region"
                    value={formData.clusterRegion}
                    onChange={(e) => setFormData({ ...formData, clusterRegion: e.target.value })}
                    variant="outlined"
                    size="small"
                    error={!!errors.clusterRegion}
                    helperText={errors.clusterRegion}
                    placeholder="us-east-1"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cluster Name"
                    value={formData.clusterName}
                    onChange={(e) => setFormData({ ...formData, clusterName: e.target.value })}
                    variant="outlined"
                    size="small"
                    error={!!errors.clusterName}
                    helperText={errors.clusterName}
                    placeholder="prod-cluster-01"
                    required
                  />
                </Grid>
              </>
            )}

            {hostingType === "Dedicated" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Private DNS"
                  value={formData.privateDns}
                  onChange={(e) => setFormData({ ...formData, privateDns: e.target.value })}
                  variant="outlined"
                  size="small"
                  error={!!errors.privateDns}
                  helperText={errors.privateDns || "AWS internal DNS format (e.g., ip-x-x-x-x.ec2.internal)"}
                  placeholder="ip-172-31-45-123.ap-southeast-1.compute.internal"
                  required
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelApp}>Cancel</Button>
          <Button onClick={handleSaveApp} variant="contained" color="primary">
            {editingIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationConfig;